import { FrameStepStorage, FrameData, FrameThresholdSettings, FrameOffsets, PatternImageData } from "../../../types";
import debounce from "lodash.debounce";
import isEqual from "lodash.isequal";
import { WorkflowManager } from "../../../WorkflowManager";
import { UnhandledBehaviorError } from "../../../util/exception";
import { calculateOffsets, frameDataCache, getFrameData } from "../../../util/frame";
import { modifySVGColors } from "../../../util/illustration";
import { svgToDataUrl } from "../../../util/crossplatform";
import { GroupCommand, UpdateFramePattern, UpdateFrameThresholdSettingsCommand } from "../../../command";

/**
 * Bounds the offsets for an image to the box, preventing
 * the user from moving the image in a way that wouldn't be intended.
 * @param newOffsets The new intended offsets for the image.
 * @param frameData The current frame information
 * @param borderWidth The width of the border added by the cropper.
 * @param mustCover When true the image sgould be bounded in such a way that it covers the entire frame at all times.
 */
export function getBoundedOffsets(
    newOffsets: FrameOffsets,
    frameData: FrameData,
    imageData: PatternImageData,
    mustCover?: boolean,
) {
    const newWidth = imageData.width * newOffsets.zoom;
    const newHeight = imageData.height * newOffsets.zoom;

    if (mustCover) {
        // Find which is smaller: framewidth / imagewidth or frameheight / imageheight
        // Then calculate the min zoom factor based on the smaller
        // of the zoom to atleast fit the image and the users new zoom level.
        const covering = newOffsets;
        const zoomMinimumBound = Math.max(frameData.width / imageData.width, frameData.height / imageData.height);
        covering.zoom = Math.max(zoomMinimumBound, newOffsets.zoom);
        const boundedWidth = imageData.width * covering.zoom;
        const boundedHeight = imageData.height * covering.zoom;
        covering.x = boundToRange(newOffsets.x, frameData.width - boundedWidth, 0);
        covering.y = boundToRange(newOffsets.y, frameData.height - boundedHeight, 0);
        return covering;
    }

    // If we don't need to cover we just make sure the users can't drag the image completely off
    // screen which may cause trouble when trying to find the image again.
    const bounded = newOffsets;
    bounded.x = boundToRange(bounded.x, -newWidth, frameData.width);
    bounded.y = boundToRange(bounded.y, -newHeight, frameData.height);

    return bounded;
}

/**
 * Bounds a value to a given range
 * @param value The value to bound
 * @param min The minimum bound that value must be above.
 * @param max The maximum bound the value must be below.
 */
function boundToRange(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export type UpdateFrameOffsets = (
    newOffsets: FrameOffsets[],
    imageData: PatternImageData,
    frameData: FrameData[],
    thresholdSettings: FrameThresholdSettings,
    onComplete?: () => void,
) => void;

export class FrameService {
    // Internal state
    private offsets: FrameOffsets[];
    private thresholdSettings: FrameThresholdSettings;
    private forceImageCover?: boolean;
    private initialZoom?: number;
    private targetElements: string[]; // A list of canvas element ids that should be updated when changes occur to this frame.

    // FIXME: Can we pull these from the element itself?
    private imageData?: PatternImageData; // The current pattern if any.
    // FIXME: Can we pull these from the element itself?
    private frameData?: FrameData[]; // The current frame if any.

    // Debounced functions

    private readonly _debouncedUpdateFrameOffsets: UpdateFrameOffsets;

    // constants

    /**
     * The calculated minimum zoom value, per frame.
     */
    public minZoomScale = [0.03];
    /**
     * The calculated maximum zoom value. Note: This is calculated based on the first frame.
     */
    public maxZoomScale = [20];

    // Listeners

    private onFrameDataChangeListeners: ((frameData: FrameData[] | undefined) => void)[];
    private onZoomChangeListeners: ((zoomValue: number[]) => void)[];

    // Workflow manager related logic
    private workflowManager?: WorkflowManager;
    private stepName?: string;

    constructor(forceImageCover?: boolean, initialZoom?: number) {
        this._debouncedUpdateFrameOffsets = debounce(this.updateFrameOffsets, 200);
        this.targetElements = [];
        this.onFrameDataChangeListeners = [];
        this.onZoomChangeListeners = [];
        this.forceImageCover = forceImageCover;
        this.initialZoom = initialZoom;
        this.thresholdSettings = {
            useThreshold: false,
            invertThreshold: false,
            threshold: 128,
            thresholdSaturation: 0.5,
        };
    }

    /**
     * When we want to connect a workflow manager to the state of the image cropper we
     * can pass it to this function. Inside we'll attach any required event listeners.
     * @param workflowManager The workflow manager to attach.
     * @param stepName The specific step we want to attach to within the manager.
     */
    connectWorkflowManager(workflowManager: WorkflowManager, stepName?: string) {
        if (stepName) {
            workflowManager.addStepSpecificStorageCallback(async (storage) => {
                if (storage) {
                    const frameStorage = storage as FrameStepStorage;
                    if (frameStorage.currentFrameSources) {
                        let changed = false;
                        for (let i = 0; i < frameStorage.currentFrameSources.length; i++) {
                            const frameSource = frameStorage.currentFrameSources[i];
                            const newFrameData = await getFrameData(frameSource);
                            const existingFrameData = this.frameData ? this.frameData[i] : undefined;
                            if (!isEqual(newFrameData, existingFrameData)) {
                                if (!this.frameData) {
                                    this.frameData = new Array(frameStorage.currentFrameSources.length);
                                }
                                this.frameData[i] = newFrameData;
                                changed = true;
                            }
                        }
                        if (changed) {
                            this.onFrameDataChangeListeners.forEach((frameDataChangeListener) =>
                                frameDataChangeListener(this.frameData),
                            );
                            if (this.frameData && this.imageData) {
                                this.recalculateOffsets(this.imageData);
                                this.updateOffsets(this.offsets);
                                this.recalculateZoomLimits(this.imageData, this.frameData);
                            }
                        }
                    }
                }
            }, stepName);
        }

        this.workflowManager = workflowManager;
        this.stepName = stepName;
    }

    /**
     * Sets the elements that should be update when changes are made to
     * the cropper that owns this service.
     * @param targetElements A list of element Ids to track
     */
    setTargetElements(targetElements: string[]) {
        this.targetElements = targetElements;
    }

    /**
     * Gets the current calculated frame data
     * @returns A FrameData object or undefined if no frame has been set.
     */
    getFrameData(): FrameData[] | undefined {
        return this.frameData;
    }

    /**
     * Sets the current frame data. Note:
     * @param paths The paths to lookup in our frame data cache.
     */
    setFrameData(paths: string[] | undefined) {
        if (!paths) {
            this.frameData = undefined;
            return;
        }
        paths.forEach((path, index) => {
            const frameData = frameDataCache.get(path);
            if (frameData) {
                if (!this.frameData) {
                    this.frameData = new Array(paths.length);
                }
                this.frameData[index] = frameData;
            }
        });
    }

    /**
     * Gets the currently set image of the frame..
     * @returns A PatternImageData object, or undefined if no image is set.
     */
    getImageData(): PatternImageData | undefined {
        return this.imageData;
    }

    /**
     * Helper function to get the src of the image of the frame, since it can be an svg with modified colors.
     * @returns A url (a data url if svg), or undefined if no image is set.
     */
    getImageSrc(): string | undefined {
        if (!this.imageData) {
            return undefined;
        }
        if (this.imageData.svg) {
            const modifiedSvg = modifySVGColors(this.imageData.svg, this.imageData.colors || {}, false);
            return svgToDataUrl(modifiedSvg);
        }
        return this.imageData.src;
    }

    /**
     * Gets the current calculated offsets of the pattern within the frame.
     * @returns A FrameOffsets object or undefined if no offsets are defined.
     */
    getOffsets(): FrameOffsets[] | undefined {
        return this.offsets;
    }

    /**
     * Updates the frame offsets explicitly.
     */
    setOffsets(offsets: FrameOffsets[]) {
        this.offsets = offsets;
    }

    /**
     * Sets the zoom of the cropper that owns this service.
     * @param zoom The new zoom value, per frame.
     * @param cX The center of zoom on x axis, per frame.
     * @param cY The center of zoom on Y axis, per frame.
     * @param onComplete A function to call when zoom changes have been completed
     */
    setZoom(zoom: number[], cX: number[], cY: number[], onComplete?: () => void) {
        if (this.imageData && this.offsets && this.frameData) {
            if (!this.offsets || this.offsets.length !== this.frameData.length) {
                this.offsets = new Array(this.frameData.length);
            }
            const calculatedOffsets = this.frameData.map((frameData, index) => {
                // Figure out the top/left displacement caused by the zoom.
                const xoff = (cX[index] - this.offsets[index].x) / this.offsets[index].zoom;
                const yoff = (cY[index] - this.offsets[index].y) / this.offsets[index].zoom;
                const dX = cX[index] - xoff * zoom[index];
                const dY = cY[index] - yoff * zoom[index];
                const calculatedOffsets = {
                    x: dX,
                    y: dY,
                    zoom: (this.imageData!.width * zoom[index]) / this.imageData!.width,
                };
                return calculatedOffsets;
            });
            // Applying the new offsets will cause the image to be bounded.
            this.updateOffsets(calculatedOffsets, onComplete);
            this.onZoomChangeListeners.forEach((zoomChangeListener) => zoomChangeListener(zoom));
        }
    }

    /**
     * Sets the image currently contained by this frame.
     * @param value The new image as an ImageData property
     * @param [recalculateOffsets=true] Optional: Enable/disable re-calculating of frame offsets. Default: `true`.
     * Note: Will always calculate when offsets have not been calculated.
     */
    setPatternData(value: PatternImageData, recalculateOffsets = true) {
        this.imageData = value;
        if (value && this.frameData) {
            if (recalculateOffsets || !this.offsets) {
                this.recalculateOffsets(value);
            }
            // Applying the new offsets will cause the image to be bounded.
            this.updateOffsets(this.offsets, undefined, true);
        }
        if (this.imageData && this.frameData) {
            this.recalculateZoomLimits(this.imageData, this.frameData);
        }
    }

    /**
     * Modify the offsets of the frame.
     * @param value The new FrameOffsets objects.
     * @param onComplete A callback, called when the modification is complete
     * @param forceUpdate When true the offsets will be updated even if they haven't changed.
     */
    updateOffsets(value: FrameOffsets[], onComplete?: () => void, forceUpdate?: boolean) {
        const patternData = this.imageData;
        // If the frame isn't correctly initialized yet just ignore..
        if (!patternData || !this.frameData) {
            return;
        }

        if (this.frameData.length !== value.length) {
            throw new UnhandledBehaviorError(
                "Frame data and offsets are not the same length. This is a bug. Please report it.",
            );
        }

        const changed = this.offsets.some((offset, index) => {
            // If there hasn't been any change let's not set or update
            // the offsets internally. Doing so will trigger re-rendering for no good reason.
            if (value[index].x === offset.x && value[index].y === offset.y && value[index].zoom === offset.zoom) {
                return false;
            }
            return true;
        });
        if (!changed && !forceUpdate) {
            onComplete && onComplete();
            return;
        }

        if (!this.offsets || this.offsets.length !== this.frameData.length) {
            this.offsets = new Array(this.frameData.length);
        }
        this.frameData.forEach((frameData, index) => {
            this.offsets[index] = getBoundedOffsets(value[index], frameData, this.imageData!, this.forceImageCover);
        });

        // Update anyone who wants to know about changes to the frame.
        this._debouncedUpdateFrameOffsets(
            this.offsets,
            patternData,
            this.frameData,
            this.thresholdSettings,
            onComplete,
        );
    }

    getThresholdSettings(): FrameThresholdSettings {
        return this.thresholdSettings;
    }

    setThresholdSettings(settings: FrameThresholdSettings) {
        this.thresholdSettings = settings;
        if (this.imageData && this.frameData) {
            this._debouncedUpdateFrameOffsets(this.offsets, this.imageData, this.frameData, settings);
        }
    }

    // Allows users of the frame service to listen to frame data changes.
    onFrameDataChanged(newListener: (frameData: FrameData[] | undefined) => void) {
        newListener(this.frameData);
        this.onFrameDataChangeListeners.push(newListener);
    }

    /**
     * Append a new listener to zoom events on this frame.
     * @param newListener
     */
    onZoom(newListener: (zoomValue: number[]) => void) {
        this.onZoomChangeListeners.push(newListener);
    }

    /**
     * Updates the offsets of the frame
     * @param newOffsets New offset object
     * @param imageData The image data
     * @param frameData The frame data
     * @param targetElements A list of elements that need updating, by ID
     * @param onComplete A callback when the operation is completed.
     */
    private updateFrameOffsets(
        newOffsets: FrameOffsets[],
        imageData: PatternImageData,
        frameData: FrameData[],
        thresholdSettings: FrameThresholdSettings,
        onComplete?: () => void,
    ) {
        if (!frameData || frameData.length === 0 || frameData.some((frame) => !frame)) {
            throw new UnhandledBehaviorError("Frame data not set. This is a bug");
        }

        if (!this.workflowManager) throw new UnhandledBehaviorError("No workflow manager set, cannot update offsets.");

        const regionElements = this.workflowManager.getRegionElements(this.stepName || "");
        const commandDispatcher = this.workflowManager.getCommandDispatcher();
        this.targetElements.forEach((elementID, index) => {
            let offsetIndex = regionElements.find((r) => r.id === elementID)?.regionIndex ?? index;
            if (offsetIndex >= newOffsets.length) {
                offsetIndex = index;
            }
            commandDispatcher(
                new GroupCommand([
                    new UpdateFramePattern(elementID, imageData, newOffsets[offsetIndex]),
                    new UpdateFrameThresholdSettingsCommand(
                        elementID,
                        thresholdSettings.useThreshold,
                        thresholdSettings.invertThreshold,
                        thresholdSettings.threshold,
                        thresholdSettings.thresholdSaturation,
                    ),
                ]),
            );
        });

        if (this.stepName) {
            this.workflowManager.updateStorage(this.stepName, {
                frameOffsetsList: newOffsets,
            });
        }

        onComplete && onComplete();
    }

    /**
     * Determines limitations of zoom based on relative size of image and frame.
     * @param imageData The image to include in calculations
     * @param frameData The frame to include in calculations.
     */
    private recalculateZoomLimits(imageData: PatternImageData, frameData: FrameData[]) {
        if (this.minZoomScale.length !== frameData.length || this.maxZoomScale.length !== frameData.length) {
            this.minZoomScale = new Array(frameData.length);
            this.maxZoomScale = new Array(frameData.length);
        }
        frameData.forEach((frame, index) => {
            const minimumScale = Math.max(frame.width / imageData.width, frame.height / imageData.height);
            if (this.forceImageCover) {
                this.minZoomScale[index] = minimumScale;
                this.maxZoomScale[index] = minimumScale * 2.5;
            } else {
                this.minZoomScale[index] = minimumScale / 10;
                this.maxZoomScale[index] = minimumScale * 2.5;
            }
        });
    }

    private recalculateOffsets(imageData: PatternImageData) {
        if (!this.frameData) return;
        if (!this.offsets || this.offsets.length !== this.frameData.length) {
            this.offsets = new Array(this.frameData.length);
        }
        this.frameData.forEach((frameData, index) => {
            this.offsets[index] = calculateOffsets(
                imageData,
                frameData,
                this.initialZoom && !this.forceImageCover ? { scale: this.initialZoom } : undefined,
                this.forceImageCover,
            );
        });
        this._debouncedUpdateFrameOffsets(this.offsets, imageData, this.frameData, this.thresholdSettings);
    }
}
